import { setProperties } from '@ember/object';
import queryString from 'query-string';

export function notifyBugsnag(error) {
  if (typeof window !== 'undefined') {
    window._bugsnagClient?.notify(error);
  }
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch (_err) {
    return value;
  }
}

export default class ClientSwitchHelper {
  #retriedTargetId = null;

  // Reads its collaborators off the owning service on every use, so an instance built
  // during a FastBoot render never holds on to torn-down injections.
  constructor(owner) {
    this.owner = owner;
  }

  get session() {
    return this.owner.session;
  }

  get store() {
    return this.owner.store;
  }

  get announcement() {
    return this.owner.announcement;
  }

  // Transfer deep links and the setup API both identify a client by hashedId, never by
  // the record's primary key.
  isCurrent(targetClientId) {
    return !targetClientId || targetClientId === this.session.currentClient?.hashedId;
  }

  async switchTo(targetClientId) {
    if (this.isCurrent(targetClientId)) {
      return false;
    }

    return this.#authenticate(targetClientId);
  }

  takeRetry(targetClientId) {
    if (!targetClientId || this.#retriedTargetId === targetClientId) {
      return false;
    }

    this.#retriedTargetId = targetClientId;
    return true;
  }

  queryClientId(transition, transferRoute) {
    if (transition?.to?.name !== transferRoute) {
      return null;
    }

    return transition.to.queryParams.clientId || null;
  }

  hasTransferRedirect(transferPath) {
    let redirect = this.session.get('data.redirect');
    if (!redirect) {
      return false;
    }

    let [path] = safeDecode(redirect).split('?');
    return path.replace(/^\//, '') === transferPath;
  }

  // A deep link that arrives through token sign-in never reaches the transfer route as a
  // transition: `application.beforeModel` stashes it and `site/home.js` consumes it later.
  // Later in-app navigations still carry that stash, so only honor it on the sign-in hand-off.
  redirectClientId(transition, transferPath) {
    let from = transition?.from?.name;
    if (from && !from.startsWith('sign-in')) {
      return null;
    }

    if (!this.hasTransferRedirect(transferPath)) {
      return null;
    }

    let [, query] = safeDecode(this.session.get('data.redirect')).split('?');
    return queryString.parse(query).clientId || null;
  }

  transferClientId(transition, transferRoute, transferPath) {
    return (
      this.queryClientId(transition, transferRoute) ??
      this.redirectClientId(transition, transferPath)
    );
  }

  canSwitchFor(transition, transferRoute, transferPath) {
    let clientId = this.transferClientId(transition, transferRoute, transferPath);

    return Boolean(clientId && !this.isCurrent(clientId) && this.#findClient(clientId));
  }

  alignedTargetId(currentId, targetClientId) {
    if (currentId && !this.isCurrent(targetClientId)) {
      return currentId;
    }

    return targetClientId;
  }

  #findClient(targetClientId) {
    let matches = client => client.hashedId === targetClientId;

    return (
      this.session.currentClientOptions?.find(matches) ||
      this.store.peekAll('treatable-client')?.find(matches) ||
      this.store.peekAll('client')?.find(matches)
    );
  }

  async #authenticate(targetClientId) {
    let client = this.#findClient(targetClientId);
    let environment = this.store.peekAll('environment').slice().at(0);

    if (!client || !environment) {
      return false;
    }

    try {
      await this.store.adapterFor('treatable-client').manage(client.id);
      setProperties(environment, { currentClient: client });
      await this.session.authenticate('authenticator:environment', environment);
      this.announcement?.fetchAnnouncements();
    } catch (error) {
      notifyBugsnag(error);
      return false;
    }

    return this.isCurrent(targetClientId);
  }
}
